/* ============================================================================
   CANCELLATION INITIATED                                             (PRD 4)

   The state that pins a member to the TOP of a list, as against suppression, which
   takes them off every list. Both are states rather than scores and neither is
   argued with.

   WHY IT EXISTS AT ALL. A member who has cancelled, asked what cancelling involves,
   or asked for the terms is the most urgent person in the building, and they are
   frequently GREEN when they ask - they have been training all along and something
   else changed. A product that ranks by attendance puts them near the bottom of the
   worst-first list on the one day that was ever going to matter. So this does not
   adjust their score. It replaces it.

   THE RULES THIS FILE ENFORCES, each from PRD 4:
     · No score and no tier, on any seat. `lifecycleOf` returns a null score.
     · Recorded the moment it is heard, by whoever heard it. Anybody may open one.
     · Only the Owner may resolve it. `canResolve` is the gate.
     · It NEVER EXPIRES. There is no age at which this clears itself, and there is
       deliberately no function here that would let one. Resolution is a person.
     · It ends in one of two ways and both are written down: they left, with an exit
       reason code, or they stayed, and what changed is recorded. `resolve` refuses
       a resolution missing the half its outcome requires.
     · No automation of any kind reaches them while it holds. Enforced by
       `reachableByAutomation`, which every sender must ask.
   ============================================================================ */
import { daysSince, todayISO } from './time.js';
/** Open means raised and not yet resolved by a human. Age is irrelevant. */
export function cancellationOpen(m) {
    const c = m?.cancellation;
    return !!c && c.resolvedAt == null;
}
/** How long they have been waiting for the Owner. Null when none is open. */
export function cancellationAge(m, today = todayISO()) {
    if (!cancellationOpen(m))
        return null;
    return daysSince(m.cancellation.askedAt, today);
}
/* EVERY OPEN ONE, LONGEST WAITING FIRST.
 *
 * Not newest first, which is the ordering a queue usually wants. The question this
 * list is asked is "who has been waiting on the Owner longest", and a member who
 * raised cancelling nine days ago and has heard nothing is a worse position to be in
 * than one who raised it this morning. Ties break on name so the order is stable
 * between renders rather than depending on how the roster happened to be sorted. */
export function openCancellations(members) {
    return members
        .filter((m) => cancellationOpen(m))
        .sort((a, b) => {
        const at = a.cancellation.askedAt;
        const bt = b.cancellation.askedAt;
        if (at !== bt)
            return at < bt ? -1 : 1;
        return a.name.localeCompare(b.name);
    });
}
/* NOTHING AUTOMATED REACHES THEM, and this is the one place that decides it.
 *
 * Every suppression rule in the product funnels through here rather than through
 * each sender remembering: a campaign, a sequence, a win-back, a reward, a milestone
 * and a challenge invitation all ask this same question. PRD 9 and 14 both say the
 * rule is enforced at the send rather than by the sender. */
export function reachableByAutomation(m) {
    if (!m)
        return false;
    if (cancellationOpen(m))
        return false;
    if (m.suppression)
        return false;
    return true;
}
/** Anybody who works at the gym may record one: it is heard, not decided. */
export function openCancellation(input) {
    const asked = input.asked.trim();
    if (!asked)
        throw new Error('what they asked is required: it prints where a score would be');
    if (!input.recordedBy.trim())
        throw new Error('who heard it is required');
    return {
        askedAt: input.askedAt ?? todayISO(),
        asked,
        recordedBy: input.recordedBy,
        resolvedAt: null,
        resolution: null,
    };
}
/** Only the Owner closes one. A seat is a preset, so this reads the role it was
 *  handed rather than assuming the nav prevented the click. */
export function canResolve(roles) {
    return !!roles?.includes('gym_admin');
}
/* BOTH ENDINGS ARE WRITTEN DOWN, and this refuses the half-written one.
 *
 * They left: an exit reason code, the same codes a lead uses, because nothing exits
 * without one. They stayed: what changed, because a member who stays after asking is
 * the single most valuable case study the gym has and "resolved" records none of it. */
export function resolve(c, resolution, today = todayISO()) {
    if (c.resolvedAt)
        throw new Error('this cancellation is already resolved');
    if (resolution.outcome === 'left' && !resolution.reasonCode) {
        throw new Error('a member who left needs an exit reason code');
    }
    if (resolution.outcome === 'stayed' && !resolution.whatChanged?.trim()) {
        throw new Error('a member who stayed needs what changed: it is the case study');
    }
    return { ...c, resolvedAt: today, resolution };
}
